import { useEffect, useMemo, useState } from "react";
import ScorecardInfoTip from "./ScorecardInfoTip";
import { SkeletonBlock } from "../Loader";
import api from "../../lib/api";
import { fmtNum, fmtMoneyShort, MONTHS_ES } from "./formatters";

/**
 * Collapsable section that pulls `/scorecard/attribution-audit` only when
 * the user opens the disclosure. Shows month-over-month rollup, a "diverging
 * only" toggle, and a CSV export so ops can reconcile paid-media reports
 * (Meta / Google) against Dynamics.
 */
export default function AttributionAuditSection({ period, filters, dateRange }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyDiverging, setOnlyDiverging] = useState(true);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const params = { year: 2026, period, limit: 5000 };
        if (period === "custom") {
          params.start_date = dateRange.start;
          params.end_date   = dateRange.end;
        }
        Object.entries(filters).forEach(([k, v]) => { if (v && v.length) params[k] = v; });
        const r = await api.get("/scorecard/attribution-audit", {
          params, paramsSerializer: { indexes: null },
        });
        if (mounted) setData(r.data);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [open, period, dateRange.start, dateRange.end,
      filters.brand, filters.model, filters.branch, filters.business_unit, filters.channel]);

  const rows = useMemo(() => {
    if (!data) return [];
    return onlyDiverging ? data.rows.filter((r) => r.diverges) : data.rows;
  }, [data, onlyDiverging]);

  const downloadCsv = () => {
    if (!data) return;
    const headers = ["mes", "fecha", "cliente", "marca", "modelo", "sucursal",
      "monto_rd", "canal_digital", "campana_digital", "campana_crm", "origen_crm", "divergente"];
    const csvRows = rows.map((r) => [
      r.sale_month, r.sale_date, r.sale_name, r.sale_brand, r.sale_model,
      r.sale_showroom, r.sale_amount, r.digital_channel, r.digital_campaign,
      r.crm_campaign || "", r.crm_origin || "", r.diverges ? "SI" : "NO",
    ].map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `atribucion-digital-vs-crm_${data.period_label.replace(/\s→\s/, "_a_").replace(/\s/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <details className="surface mt-6"
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid="attribution-audit-section">
      <summary className="p-6 cursor-pointer"
        style={{ listStyle: "none", userSelect: "none" }}
        data-testid="attribution-audit-toggle">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-base font-extrabold inline-flex items-center gap-1.5">
              Auditoría de Atribución · Digital vs CRM
              <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8" }}>{open ? "▾" : "▸"}</span>
              <ScorecardInfoTip label="Auditoría de Atribución"
                formula="Cada venta convertida se cruza con leads_raw (CRM Dynamics) por email/nombre/teléfono para exponer la campaña que el cliente reportó en el CRM."
                extra="El scorecard prioriza el UTM digital. Esta tabla queda solo para reconciliación mes a mes entre reportes de Meta/Google y Dynamics."
                source="sales × leads_digital × leads_raw" />
            </h3>
            <p className="text-[11px] text-gray-500 mt-1">
              Compara la campaña digital (UTM del pixel) con la campaña que el CRM registró para el mismo cliente.
              {" "}Click para {open ? "cerrar" : "abrir"}.
            </p>
          </div>
          {open && data && (
            <button onClick={(e) => { e.preventDefault(); downloadCsv(); }}
              className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-md"
              style={{ background: "#f4c400", color: "#111827",
                border: "1px solid #eab308", cursor: "pointer" }}
              data-testid="attribution-audit-csv">
              📥 Descargar CSV ({rows.length} filas)
            </button>
          )}
        </div>
      </summary>

      <div className="px-6 pb-6" style={{ borderTop: "1px solid #e2e8f0" }}>
        {loading && !data && (
          <div className="py-4">
            <SkeletonBlock height={64} testId="audit-skeleton-summary" />
            <div className="mt-3"><SkeletonBlock height={200} testId="audit-skeleton-table" /></div>
          </div>
        )}
        {loading && data && (
          <div className="flex items-center gap-2 py-2 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
            <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-yellow-400 rounded-full animate-spin" />
            Actualizando auditoría…
          </div>
        )}
        {data && (
          <>
            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 mb-4"
              data-testid="attribution-summary">
              <MiniStat label="Conversiones digitales" value={fmtNum(data.summary.total_digital_conversions)} />
              <MiniStat label="Con registro CRM" value={fmtNum(data.summary.with_crm_record)}
                sub={`${data.summary.crm_coverage_pct}% cobertura`} />
              <MiniStat label="UTM divergente" value={fmtNum(data.summary.diverging_utm)}
                accent="#dc2626"
                sub={`${data.summary.diverging_utm && data.summary.with_crm_record
                  ? ((data.summary.diverging_utm / data.summary.with_crm_record) * 100).toFixed(0) : 0}% de los con CRM`} />
              <MiniStat label="Período" value={data.period_label} accent="#8b5cf6" />
            </div>

            {/* Monthly rollup — full-width bar chart for better readability. */}
            {data.monthly && data.monthly.length > 0 && (
              <div className="mb-4 p-4 rounded-lg" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">
                    Divergencias por mes
                  </div>
                  <div className="text-[9px] text-gray-400">
                    {data.monthly.length} meses · barra = casos con UTM divergente
                  </div>
                </div>
                <div
                  className="grid gap-2"
                  style={{
                    // One column per month, spread edge-to-edge across the
                    // full section width — much clearer at a glance than the
                    // previous 32px flex-wrap cluster.
                    gridTemplateColumns: `repeat(${data.monthly.length}, minmax(0, 1fr))`,
                    alignItems: "end",
                  }}>
                  {data.monthly.map((m) => {
                    const pct = m.total ? (m.diverging / m.total * 100) : 0;
                    return (
                      <div key={m.month} className="flex flex-col items-center gap-1"
                        data-testid={`audit-month-${m.month}`}
                        title={`${m.label}: ${fmtNum(m.diverging)} divergentes de ${fmtNum(m.total)} (${pct.toFixed(1)}%)`}>
                        <div className="text-[11px] font-bold text-red-600 tabular-nums">
                          {fmtNum(m.diverging)}
                        </div>
                        <div style={{
                          width: "100%", height: 100, background: "#e2e8f0",
                          borderRadius: 4, position: "relative", overflow: "hidden",
                        }}>
                          <div style={{
                            position: "absolute", bottom: 0, left: 0, right: 0,
                            height: `${Math.max(3, pct)}%`,
                            background: "linear-gradient(180deg, #ef4444 0%, #dc2626 100%)",
                            transition: "height 0.4s ease",
                          }} />
                        </div>
                        <div className="text-[10px] font-bold text-gray-600">{m.label}</div>
                        <div className="text-[9px] text-gray-400 tabular-nums">{pct.toFixed(0)}%</div>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[10px] text-gray-500 mt-3">
                  Barras rojas: casos donde la campaña del CRM difiere de la campaña digital atribuida al mismo cliente.
                </div>
              </div>
            )}

            {/* Toggle */}
            <div className="flex items-center gap-4 mb-3 flex-wrap">
              <label className="inline-flex items-center gap-2 text-xs font-bold text-gray-700 cursor-pointer">
                <input type="checkbox" checked={onlyDiverging}
                  onChange={(e) => setOnlyDiverging(e.target.checked)}
                  data-testid="audit-only-diverging" />
                Mostrar solo casos con UTM divergente
              </label>
              <div className="text-[10px] text-gray-400 ml-auto">
                {fmtNum(rows.length)} filas visibles · máximo 5,000
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-lg"
              style={{ border: "1px solid #e2e8f0", maxHeight: 480 }}>
              <table className="w-full text-xs" data-testid="attribution-audit-table">
                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr style={{ background: "#0f1218", color: "#f4c400" }}>
                    {["Mes", "Cliente", "Marca / Modelo", "Sucursal", "Monto",
                      "Digital", "CRM", "Origen CRM"].map((h) => (
                      <th key={h} className="text-left px-3 py-2 font-extrabold text-[10px] uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan="8" className="text-center text-gray-400 py-4">Sin filas para mostrar</td></tr>
                  )}
                  {rows.slice(0, 500).map((r, i) => (
                    <tr key={i} style={{
                      borderTop: "1px solid #f1f5f9",
                      background: r.diverges ? "#fef2f2" : (i % 2 === 0 ? "#ffffff" : "#fafafa"),
                    }}>
                      <td className="px-3 py-1.5 font-bold text-gray-700 tabular-nums">
                        {r.sale_month ? MONTHS_ES[r.sale_month - 1] : "—"}
                      </td>
                      <td className="px-3 py-1.5 truncate" style={{ maxWidth: 180 }} title={r.sale_name}>
                        {r.sale_name || "—"}
                      </td>
                      <td className="px-3 py-1.5 truncate text-gray-600" style={{ maxWidth: 160 }}>
                        {r.sale_brand} / {r.sale_model}
                      </td>
                      <td className="px-3 py-1.5 text-[10px] text-gray-500 truncate" style={{ maxWidth: 100 }}>
                        {r.sale_showroom}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-bold text-gray-800">
                        {fmtMoneyShort(r.sale_amount)}
                      </td>
                      <td className="px-3 py-1.5 truncate" style={{ maxWidth: 180 }} title={r.digital_campaign}>
                        <div className="font-bold text-gray-900 truncate">{r.digital_campaign || "—"}</div>
                        <div className="text-[9px] text-gray-500">{r.digital_channel}</div>
                      </td>
                      <td className="px-3 py-1.5 truncate" style={{ maxWidth: 180 }} title={r.crm_campaign}>
                        <div className={`font-bold truncate ${r.diverges ? "text-red-700" : "text-gray-900"}`}>
                          {r.crm_campaign || "—"}
                        </div>
                        {r.diverges && (
                          <div className="text-[9px] font-bold text-red-600 uppercase tracking-wider">
                            Divergente
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-[10px] text-gray-500 truncate" style={{ maxWidth: 120 }}>
                        {r.crm_origin || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 500 && (
                <div className="text-center text-[10px] text-gray-400 py-2"
                  style={{ background: "#fafafa", borderTop: "1px solid #e2e8f0" }}>
                  Mostrando primeras 500 filas · descarga CSV para ver las {fmtNum(rows.length)} totales.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </details>
  );
}

function MiniStat({ label, value, sub, accent }) {
  return (
    <div className="p-3 rounded-lg" style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
      <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      <div className="text-xl font-extrabold tabular-nums"
        style={{ color: accent || "#111827", fontFamily: "Manrope, sans-serif", letterSpacing: "-0.02em" }}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}
